import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';

export type DomainBrandId = 'mega-ca' | 'mega-inc' | 'sigma-industrial';

/** Bloque de contacto por región: una etiqueta y una o más líneas (dirección, teléfonos o correos). */
export interface ContactRegionBlock {
  regionLabel: string;
  lines: string[];
}

/**
 * Marca y datos de contacto según el dominio.
 * - sigmaindustrial → Sigma Industrial Inc (Panamá), logo img_logoSigma, acentos azul #045e98.
 * - mega-ca → Maquinarias Mega (Venezuela).
 * - resto → Mega Inc (USA).
 * En localhost usar environment.forceDomainBrand.
 */
@Injectable({
  providedIn: 'root',
})
export class DomainBrandService {
  readonly brandId: DomainBrandId;

  /** true si el sitio se sirve bajo dominio mega-ca */
  readonly isMegaCa: boolean;

  /** Ruta base del logo en assets, sin extensión (.png / .webp) */
  readonly logoBase: string;

  /** Direcciones agrupadas por región (cada bloque = una etiqueta + una o más líneas). */
  readonly addressBlocks: ContactRegionBlock[];

  /** Teléfonos agrupados por región (p. ej. varios números bajo una sola etiqueta «Panamá»). */
  readonly phoneBlocks: ContactRegionBlock[];

  /** Correos agrupados por región. */
  readonly salesEmailBlocks: ContactRegionBlock[];

  /** Texto legal en el pie */
  readonly footerLegalName: string;

  /** Título del documento (pestaña del navegador) */
  readonly documentTitle: string;

  /** Ruta del favicon bajo assets (relativa a la raíz de la app) */
  readonly faviconHref: string;

  readonly faviconType: 'image/x-icon' | 'image/png';

  constructor() {
    this.brandId = this.resolveBrandId();

    this.isMegaCa = this.brandId === 'mega-ca';

    switch (this.brandId) {
      case 'sigma-industrial':
        this.logoBase = 'assets/img/img_logoSigma';
        this.addressBlocks = [
          {
            regionLabel: 'Estados Unidos',
            lines: ['6010 NW 99th Ave UNIT 111 Doral - FL 33178'],
          },
          {
            regionLabel: 'Panamá',
            lines: [
              'Calle Ramón Arias, Edificio Ropardi, Corregimiento de Bella Vista, Ciudad de Panamá.',
            ],
          },
        ];
        this.phoneBlocks = [
          { regionLabel: 'Estados Unidos', lines: ['+1 (786) 629-6226'] },
          {
            regionLabel: 'Panamá',
            lines: ['+507 392-0969', '+507 392-0979'],
          },
        ];
        this.footerLegalName = 'Sigma Industrial Inc';
        this.documentTitle = 'Sigma Industrial Inc';
        this.faviconHref = 'assets/img/favicon.png';
        this.faviconType = 'image/png';
        this.salesEmailBlocks = [
          { regionLabel: 'Estados Unidos', lines: ['sales@sigmaindustrial.com'] },
          { regionLabel: 'Panamá', lines: ['ventas@sigmaindustrial.com'] },
        ];
        break;
      case 'mega-ca':
        this.logoBase = 'assets/img/logoMega';
        this.addressBlocks = [
          {
            regionLabel: 'Venezuela',
            lines: [
              'Zona Industrial Guayabal, Av. C, parcela 1. Edif. Mega. Guarenas, Edo. Miranda',
            ],
          },
        ];
        this.phoneBlocks = [
          { regionLabel: 'Venezuela', lines: ['+58 212-300-44-80'] },
        ];
        this.footerLegalName = 'Maquinarias Mega, C.A.';
        this.documentTitle = 'Maquinarias Mega';
        this.faviconHref = 'assets/img/favicon-mega-ca.ico';
        this.faviconType = 'image/x-icon';
        this.salesEmailBlocks = [
          { regionLabel: 'Venezuela', lines: ['sales@mega-ca.com'] },
        ];
        break;
      case 'mega-inc':
        this.logoBase = 'assets/img/logoMegaInc';
        this.addressBlocks = [
          {
            regionLabel: 'Estados Unidos',
            lines: ['6010 NW 99th Ave, Unit 111, Doral, FL 33178'],
          },
        ];
        this.phoneBlocks = [
          { regionLabel: 'Estados Unidos', lines: ['+1 (786) 629-6226'] },
        ];
        this.footerLegalName = 'Mega Inc';
        this.documentTitle = 'Mega Inc';
        this.faviconHref = 'assets/img/favicon.png';
        this.faviconType = 'image/png';
        this.salesEmailBlocks = [
          { regionLabel: 'Estados Unidos', lines: ['sales@mega-ca.com'] },
        ];
        break;
    }
  }

  private resolveBrandId(): DomainBrandId {
    type Force = 'mega-ca' | 'maquinariasmega' | 'sigmaindustrial' | null | undefined;

    const forced = (environment as { forceDomainBrand?: Force }).forceDomainBrand;

    if (forced === 'sigmaindustrial') {
      return 'sigma-industrial';
    }
    if (forced === 'mega-ca') {
      return 'mega-ca';
    }
    if (forced === 'maquinariasmega') {
      return 'mega-inc';
    }
    if (typeof window === 'undefined' || !window.location?.hostname) {
      return 'mega-inc';
    }

    const h = window.location.hostname.toLowerCase();

    if (h.includes('sigmaindustrial')) {
      return 'sigma-industrial';
    }
    if (h.includes('mega-ca')) {
      return 'mega-ca';
    }

    return 'mega-inc';
  }

  /**
   * Años completos de trayectoria desde diciembre de 1986 (aniversario el 1 de diciembre de cada año).
   */
  get groupTrajectoryYears(): number {
    const foundingYear = 1986;
    const foundingMonth = 11; // December (0-based)
    const now = new Date();
    let years = now.getFullYear() - foundingYear;
    if (now < new Date(now.getFullYear(), foundingMonth, 1)) {
      years -= 1;
    }
    return Math.max(0, years);
  }
}
